import { useEffect, useState } from 'react';
import { apiFetch, useAuthStore } from '@/stores/authStore';
import type { AgentStats } from '@/lib/bots/edge';

/**
 * Polls the edge fleet and groups it by ticker, for the Value Dashboard
 * bot-presence markers (row chips + sidebar). One shared poll feeds every
 * marker. Only fetches for callers who can see bots (bot-access traders or
 * managers/admins) — others get an empty map and make no request, so the gated
 * /api/edge surface never 403-spams. Degrades to empty + configured=false when
 * the edge env is unset (503).
 */
export function useEdgeBotsByTicker(): {
  byTicker: Map<string, AgentStats[]>;
  configured: boolean;
} {
  const canView = useAuthStore((s) => {
    const u = s.user;
    if (!u) return false;
    return (u.botAccess ?? false) || u.role === 'manager' || u.role === 'admin';
  });
  const [byTicker, setByTicker] = useState<Map<string, AgentStats[]>>(() => new Map());
  const [configured, setConfigured] = useState(true);

  useEffect(() => {
    if (!canView) {
      setByTicker(new Map());
      return;
    }
    let cancelled = false;
    const poll = async () => {
      try {
        const res = await apiFetch('/api/edge/agents');
        if (res.status === 503) {
          if (!cancelled) {
            setConfigured(false);
            setByTicker(new Map());
          }
          return;
        }
        if (!res.ok) return;
        const data = (await res.json()) as { agents?: AgentStats[] };
        if (cancelled) return;
        const m = new Map<string, AgentStats[]>();
        for (const a of data.agents ?? []) {
          const arr = m.get(a.ticker) ?? [];
          arr.push(a);
          m.set(a.ticker, arr);
        }
        setByTicker(m);
        setConfigured(true);
      } catch {
        /* keep the last good map */
      }
    };
    void poll();
    // Presence doesn't need 1s — the live race-board (Position Tracker) polls
    // fast; markers just need "is there a bot here".
    const t = setInterval(poll, 5000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [canView]);

  return { byTicker, configured };
}
